import React, { useState } from "react";
import {
  CloudArrowUpIcon,
  XMarkIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/solid";
import {
  extractAssetsFromDocument,
  createManualAsset,
  ASSET_CLASS_TO_TYPE,
} from "../api/manualAssetsApi";

// All canonical AssetClass values — kept in sync with Backend models.
const ASSET_CLASSES = [
  "equity",
  "etf",
  "mutual_fund",
  "bond",
  "crypto",
  "cash",
  "real_estate",
  "commodity",
  "private",
  "collectible",
  "debt",
  "other",
];

const CURRENCIES = ["USD", "INR", "EUR", "GBP", "AED", "SGD", "AUD", "CAD", "JPY", "HKD"];

// Three-stage flow inside the modal: upload → review → save
//   upload  : drop / pick a PDF, send it to the backend
//   review  : edit / select / deselect proposed rows
//   saving  : creating the chosen rows
const DocumentUploadModal = ({ open, onClose, onSaved }) => {
  const [stage, setStage] = useState("upload");
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [drag, setDrag] = useState(false);
  const [proposed, setProposed] = useState([]); // each row gets a `_keep` flag
  const [savingCount, setSavingCount] = useState(0);

  if (!open) return null;

  const reset = () => {
    setStage("upload");
    setProposed([]);
    setError("");
    setWarning("");
    setSavingCount(0);
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setError("Only PDF files are supported.");
      return;
    }
    setError("");
    setWarning("");
    setStage("uploading");
    try {
      const res = await extractAssetsFromDocument(file);
      const rows = (res.data?.assets || []).map((a, i) => ({
        ...a,
        _id: i,
        _keep: true,
      }));
      setProposed(rows);
      if (res.data?.warning) setWarning(res.data.warning);
      setStage("review");
      if (rows.length === 0 && !res.data?.warning) {
        setWarning("No assets were extracted. Try a different document or add manually.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Upload failed.";
      setError(detail);
      setStage("upload");
    }
  };

  const updateRow = (id, patch) => {
    setProposed((curr) =>
      curr.map((r) => (r._id === id ? { ...r, ...patch } : r)),
    );
  };

  const toggleAll = (keep) => {
    setProposed((curr) => curr.map((r) => ({ ...r, _keep: keep })));
  };

  const onConfirm = async () => {
    const toSave = proposed.filter((r) => r._keep && r.name?.trim());
    if (toSave.length === 0) {
      setError("Select at least one row.");
      return;
    }
    setError("");
    setStage("saving");
    let saved = 0;
    for (const row of toSave) {
      try {
        await createManualAsset({
          name: row.name.trim(),
          symbol: (row.symbol || "").trim(),
          asset_class: row.asset_class || "other",
          quantity: Number(row.quantity) || 1,
          unit: row.unit || "",
          cost_basis: Number(row.cost_basis) || 0,
          current_value: Number(row.current_value) || 0,
          currency: (row.currency || "USD").toUpperCase(),
          notes: row.notes || "",
        });
        saved += 1;
        setSavingCount(saved);
      } catch (err) {
        // Keep going — log but don't abort.
        console.error("Could not save", row.name, err);
      }
    }
    onSaved?.(saved);
    close();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    handleFile(e.dataTransfer?.files?.[0]);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <div className="w-full max-w-3xl rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 shadow-2xl flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10">
          <div>
            <h3 className="text-lg font-bold">Upload a document</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Drop a brokerage statement, FD certificate, or property deed and we'll
              propose manual-asset rows for review.
            </p>
          </div>
          <button onClick={close} className="text-gray-500 hover:text-red-500">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* ─── BODY ─── */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 text-red-500 border border-red-500/40 text-sm">
              {error}
            </div>
          )}
          {warning && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/40 text-sm">
              <ExclamationTriangleIcon className="w-4 h-4 mt-0.5" />
              <span>{warning}</span>
            </div>
          )}

          {stage === "upload" && (
            <label
              onDragOver={(e) => {
                e.preventDefault();
                setDrag(true);
              }}
              onDragLeave={() => setDrag(false)}
              onDrop={onDrop}
              className={`flex flex-col items-center justify-center text-center p-10 rounded-xl border-2 border-dashed cursor-pointer transition ${
                drag
                  ? "border-indigo-500 bg-indigo-500/10"
                  : "border-gray-300 dark:border-gray-700 hover:border-indigo-500/60 hover:bg-indigo-500/5"
              }`}
            >
              <CloudArrowUpIcon className="w-10 h-10 text-indigo-500 mb-3" />
              <p className="font-bold mb-1">Drop a PDF or click to choose</p>
              <p className="text-xs text-gray-500">
                Brokerage statement, mutual-fund summary, FD certificate, property deed —
                anything that lists assets you own.
              </p>
              <p className="text-[10px] text-gray-500 mt-3">
                Max 8 MB · text-based PDFs only (scanned images won't extract)
              </p>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </label>
          )}

          {stage === "uploading" && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mb-4" />
              <p className="text-sm text-gray-600 dark:text-gray-300 font-semibold">
                Reading the document…
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Lens is extracting positions from the PDF.
              </p>
            </div>
          )}

          {stage === "review" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm">
                  <DocumentTextIcon className="w-4 h-4 inline mr-1 text-indigo-500" />
                  Found <span className="font-bold">{proposed.length}</span> assets.
                  Review, edit, deselect any that are wrong, then save.
                </p>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => toggleAll(true)}
                    className="text-[11px] font-semibold text-indigo-500 hover:underline"
                  >
                    Select all
                  </button>
                  <span className="text-[11px] text-gray-400">·</span>
                  <button
                    type="button"
                    onClick={() => toggleAll(false)}
                    className="text-[11px] font-semibold text-gray-500 hover:underline"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {proposed.map((row) => (
                  <div
                    key={row._id}
                    className={`p-3 rounded-lg border transition ${
                      row._keep
                        ? "border-emerald-500/40 bg-emerald-500/5"
                        : "border-gray-300 dark:border-gray-700 opacity-60"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={!!row._keep}
                        onChange={(e) => updateRow(row._id, { _keep: e.target.checked })}
                        className="mt-1 w-4 h-4 accent-emerald-500"
                      />
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 min-w-0">
                        <input
                          value={row.name || ""}
                          onChange={(e) => updateRow(row._id, { name: e.target.value })}
                          placeholder="Name"
                          className="px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm font-semibold"
                        />
                        <select
                          value={row.asset_class || "other"}
                          onChange={(e) =>
                            updateRow(row._id, { asset_class: e.target.value })
                          }
                          className="px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm"
                        >
                          {ASSET_CLASSES.map((c) => (
                            <option key={c} value={c}>
                              {ASSET_CLASS_TO_TYPE[c] || c}
                            </option>
                          ))}
                        </select>
                        <input
                          inputMode="decimal"
                          value={row.cost_basis ?? ""}
                          onChange={(e) =>
                            updateRow(row._id, { cost_basis: e.target.value })
                          }
                          placeholder="Cost basis"
                          className="px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm"
                        />
                        <input
                          inputMode="decimal"
                          value={row.current_value ?? ""}
                          onChange={(e) =>
                            updateRow(row._id, { current_value: e.target.value })
                          }
                          placeholder="Current value"
                          className="px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm"
                        />
                        <select
                          value={row.currency || "USD"}
                          onChange={(e) =>
                            updateRow(row._id, { currency: e.target.value })
                          }
                          className="px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm"
                        >
                          {CURRENCIES.map((c) => (
                            <option key={c}>{c}</option>
                          ))}
                        </select>
                        <input
                          value={row.symbol || ""}
                          onChange={(e) =>
                            updateRow(row._id, { symbol: e.target.value })
                          }
                          placeholder="Symbol (optional)"
                          className="px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm"
                        />
                      </div>
                    </div>
                    {row.notes && (
                      <p className="text-[11px] text-gray-500 italic mt-1.5 ml-7">
                        {row.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {stage === "saving" && (
            <div className="flex flex-col items-center justify-center py-16">
              <CheckCircleIcon className="w-10 h-10 text-emerald-500 mb-3" />
              <p className="text-sm font-semibold">Saving…</p>
              <p className="text-xs text-gray-500 mt-1">
                Created {savingCount} of{" "}
                {proposed.filter((r) => r._keep).length}
              </p>
            </div>
          )}
        </div>

        {/* ─── FOOTER ─── */}
        {stage === "review" && (
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-white/10">
            <button
              onClick={() => setStage("upload")}
              className="px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 text-sm"
            >
              Back
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold"
            >
              Save {proposed.filter((r) => r._keep).length} asset
              {proposed.filter((r) => r._keep).length === 1 ? "" : "s"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentUploadModal;
